import { Navigate, Outlet } from 'react-router'

import LegacyIssuesRedirect from '@/components/dashboard/organisation/non-conformity/legacy-issues-redirect'
import { ISSUES_TAB } from '@/constant/issues'
import { Feature, Role } from '@/enum/global.enum'
import type { IRoute } from '@/interface/base.interface'
import LazyLoading from '@/LazyLoading'
import { lazy } from 'react'

// ========================================
// COMMON ROUTES
// ========================================
const DashboardPage = LazyLoading(lazy(() => import('@/pages/dashboard/dashboard')))
const UpdatePassword = LazyLoading(lazy(async () => await import('@/pages/dashboard/common/update-password')))
const Profile = LazyLoading(lazy(async () => await import('@/pages/dashboard/common/profile')))
const MfaSetupPage = LazyLoading(lazy(async () => await import('@/pages/dashboard/common/mfa-setup')))
const Notifications = LazyLoading(lazy(async () => await import('@/pages/dashboard/organisation/notification')))
const LoginLogs = LazyLoading(lazy(async () => await import('@/pages/dashboard/common/loginlogs')))
const ActivityLogs = LazyLoading(lazy(async () => await import('@/pages/dashboard/common/activity-logs')))
const ShowAllUserActivityLogs = LazyLoading(
  lazy(async () => await import('@/pages/dashboard/common/all-user-activity-logs'))
)
const PageNotFound = LazyLoading(lazy(async () => await import('@/pages/dashboard/common/404')))
const UserManual = LazyLoading(lazy(async () => await import('@/pages/dashboard/common/user-manual')))

// ========================================
// ORGANISATION ROUTES
// ========================================
const Onboarding = LazyLoading(lazy(async () => await import('@/pages/dashboard/organisation/on-boarding')))
const OrganisationUserManageBugReport = LazyLoading(
  lazy(async () => await import('@/pages/dashboard/organisation/manage-bug-report'))
)
const ManageUserPage = LazyLoading(lazy(async () => await import('@/pages/dashboard/organisation/manage-user')))
const ManageOrganisation = LazyLoading(
  lazy(async () => await import('@/pages/dashboard/organisation/manage-organisation'))
)
const Policy = LazyLoading(lazy(async () => await import('@/pages/dashboard/organisation/policy/policy')))
const PolicyDashboard = LazyLoading(
  lazy(async () => await import('@/pages/dashboard/organisation/policy/policy-dashboard'))
)
const RiskList = LazyLoading(lazy(async () => await import('@/pages/dashboard/organisation/risk/risk-list')))
const AddRisk = LazyLoading(lazy(async () => await import('@/pages/dashboard/organisation/risk/add-risk')))
const PlanOfAction = LazyLoading(lazy(async () => await import('@/pages/dashboard/organisation/risk/plan-of-action')))
const RiskDashboard = LazyLoading(lazy(async () => await import('@/pages/dashboard/organisation/risk/risk-dashboard')))
const RiskEscalation = LazyLoading(
  lazy(async () => await import('@/pages/dashboard/organisation/risk/risk-escalation'))
)
const ResidualRiskReport = LazyLoading(
  lazy(async () => await import('@/pages/dashboard/organisation/risk/residual-risk-report'))
)
const AddIncident = LazyLoading(
  lazy(async () => await import('@/components/dashboard/organisation/risk/incident/add-incident'))
)
const IncidentList = LazyLoading(
  lazy(async () => await import('@/pages/dashboard/organisation/risk/incident/incident'))
)
const IncidentDashboard = LazyLoading(
  lazy(async () => await import('@/pages/dashboard/organisation/risk/incident/incident-dashboard'))
)
const AddServiceProvider = LazyLoading(
  lazy(async () => await import('@/pages/dashboard/organisation/service-provider/add-service-provider'))
)
const ServiceProviderList = LazyLoading(
  lazy(async () => await import('@/pages/dashboard/organisation/service-provider/service-provider'))
)
const ServiceProviderImpactAssessment = LazyLoading(
  lazy(async () => await import('@/pages/dashboard/organisation/service-provider/service-provider-impact-assessment'))
)
const ServiceProviderRiskPosture = LazyLoading(
  lazy(async () => await import('@/pages/dashboard/organisation/service-provider/service-provider-risk-posture'))
)
const AssessmentLibrary = LazyLoading(
  lazy(
    async () => await import('@/pages/dashboard/organisation/service-provider/questionnaire-ibrary/assessment-library')
  )
)
const AddAssessmentLibrary = LazyLoading(
  lazy(
    async () =>
      await import('@/pages/dashboard/organisation/service-provider/questionnaire-ibrary/add-assessment-library')
  )
)
const ServiceProviderAssessment = LazyLoading(
  lazy(async () => await import('@/pages/dashboard/organisation/service-provider/service-provider-assessment'))
)
const AddServiceProviderAssessment = LazyLoading(
  lazy(async () => await import('@/components/dashboard/organisation/service-provider/assessment/add-assessment'))
)
const QuestionnaireBuilder = LazyLoading(
  lazy(async () => await import('@/pages/dashboard/organisation/service-provider/questionnaire-builder'))
)
const ServiceProviderDashboard = LazyLoading(
  lazy(async () => await import('@/pages/dashboard/organisation/service-provider/service-provider-dashboard'))
)
const IncomingQuestionnaireList = LazyLoading(
  lazy(
    async () =>
      await import('@/pages/dashboard/organisation/service-provider/incoming-questionnaire/incoming-questionnaire')
  )
)
const IncomingQuestionnaireDetail = LazyLoading(
  lazy(
    async () =>
      await import(
        '@/pages/dashboard/organisation/service-provider/incoming-questionnaire/incoming-questionnaire-detail'
      )
  )
)
const AnswerIncomingQuestionnaire = LazyLoading(
  lazy(
    async () =>
      await import(
        '@/pages/dashboard/organisation/service-provider/incoming-questionnaire/answer-incoming-questionnaire'
      )
  )
)
const ResourceHelpDesk = LazyLoading(lazy(async () => await import('@/pages/dashboard/organisation/resource')))
const AssessmentCards = LazyLoading(
  lazy(async () => await import('@/pages/dashboard/organisation/compliance/assessment'))
)
const ArchiveAssessment = LazyLoading(
  lazy(async () => await import('@/pages/dashboard/organisation/compliance/archive-assessment'))
)
const ComplianceReport = LazyLoading(
  lazy(async () => await import('@/pages/dashboard/organisation/compliance/compliance-report'))
)
const AssessmentDetail = LazyLoading(
  lazy(async () => await import('@/pages/dashboard/organisation/compliance/assessment-detail'))
)
const CspGAPAssessmentDetail = LazyLoading(
  lazy(async () => await import('@/pages/dashboard/organisation/compliance/csp-gap-assessment-detail'))
)
const PciAssessmentDetail = LazyLoading(
  lazy(async () => await import('@/pages/dashboard/organisation/compliance/pci-assessment-detail'))
)
const PciSAQAssessmentDetail = LazyLoading(
  lazy(async () => await import('@/pages/dashboard/organisation/compliance/pci-saq-assessment-detail'))
)
const StartAssessment = LazyLoading(
  lazy(async () => await import('@/pages/dashboard/organisation/compliance/start-assessment'))
)
const StartPciAssessment = LazyLoading(
  lazy(async () => await import('@/pages/dashboard/organisation/compliance/start-pci-assessment'))
)
const StartPciRocAssessment = LazyLoading(
  lazy(async () => await import('@/pages/dashboard/organisation/compliance/start-pci-roc-assessment'))
)
const StartPciSAQAssessment = LazyLoading(
  lazy(async () => await import('@/pages/dashboard/organisation/compliance/start-pci-saq-assessment'))
)
const PciScopingConfiguration = LazyLoading(
  lazy(async () => await import('@/pages/dashboard/organisation/compliance/pci-scoping-configuration/index'))
)
const IsmScopingConfiguration = LazyLoading(
  lazy(async () => await import('@/pages/dashboard/organisation/compliance/ism-scoping-configuration'))
)
const SaqReport = LazyLoading(
  lazy(async () => await import('@/pages/dashboard/organisation/compliance/pci-saq-report'))
)
const PciScopingConfigurationAccessType = LazyLoading(
  lazy(async () => await import('@/pages/dashboard/organisation/settings/pci/pci-scoping/people/access-type'))
)
const PciScopingConfigurationTrainingType = LazyLoading(
  lazy(async () => await import('@/pages/dashboard/organisation/settings/pci/pci-scoping/people/training-type'))
)
const PciScopingConfigurationDataHandlingType = LazyLoading(
  lazy(async () => await import('@/pages/dashboard/organisation/settings/pci/pci-scoping/process/data-handling-type'))
)
const PciScopingConfigurationFrequencyType = LazyLoading(
  lazy(async () => await import('@/pages/dashboard/organisation/settings/pci/pci-scoping/process/frequency-type'))
)
const Reporting = LazyLoading(lazy(async () => await import('@/pages/dashboard/organisation/compliance/reporting')))
const StartIsmIrapAssessment = LazyLoading(
  lazy(async () => await import('@/pages/dashboard/organisation/compliance/start-ism-irap-assessment'))
)
const StartIsmGapAssessment = LazyLoading(
  lazy(async () => await import('@/pages/dashboard/organisation/compliance/start-ism-gap-assessment'))
)
const StartCisAssessment = LazyLoading(
  lazy(async () => await import('@/pages/dashboard/organisation/compliance/start-cis-assessment'))
)
const StartSoc2Assessment = LazyLoading(
  lazy(async () => await import('@/pages/dashboard/organisation/compliance/start-soc2-assessment'))
)
const StartCspAssessment = LazyLoading(
  lazy(async () => await import('@/pages/dashboard/organisation/compliance/start-csp-assessment'))
)
const CspDashboard = LazyLoading(
  lazy(async () => await import('@/pages/dashboard/organisation/compliance/csp-dashboard'))
)
const CspAssessmentReview = LazyLoading(
  lazy(async () => await import('@/pages/dashboard/organisation/compliance/csp-assessment-review'))
)
const CspAssessmentAttestation = LazyLoading(
  lazy(async () => await import('@/pages/dashboard/organisation/compliance/csp-assessment-attestation'))
)
const CspAssessmentExceptionRequest = LazyLoading(
  lazy(async () => await import('@/pages/dashboard/organisation/compliance/csp-assessment-exception-request'))
)
const CspReportingCalendar = LazyLoading(
  lazy(async () => await import('@/pages/dashboard/organisation/compliance/csp-reporting-calendar'))
)
const IsmIrapAssessmentDetail = LazyLoading(
  lazy(async () => await import('@/pages/dashboard/organisation/compliance/ism-irap-assessment-detail'))
)
const IsmPrincipleAssessmentDetail = LazyLoading(
  lazy(async () => await import('@/pages/dashboard/organisation/compliance/ism-principle-assessment-detail'))
)
const NonConformityDetail = LazyLoading(
  lazy(async () => await import('@/pages/dashboard/organisation/compliance/non-conformity-detail'))
)
const IssuesPage = LazyLoading(
  lazy(async () => await import('@/pages/dashboard/organisation/compliance/issues/issues-page'))
)
const StatementOfApplicabilityPage = LazyLoading(
  lazy(async () => await import('@/pages/dashboard/organisation/compliance/nc-ofi-soa/statement-of-applicability-page'))
)
const AssessmentsLogs = LazyLoading(
  lazy(async () => await import('@/pages/dashboard/organisation/compliance/assessment-logs'))
)
const ReportLibrary = LazyLoading(
  lazy(async () => await import('@/pages/dashboard/organisation/compliance/report-library'))
)
const ServiceProviderReportLibrary = LazyLoading(
  lazy(async () => await import('@/pages/dashboard/organisation/service-provider/service-provider-report-library'))
)
const AiRiskStepperPage = LazyLoading(
  lazy(async () => await import('@/pages/dashboard/organisation/service-provider/ai-risk-stepper'))
)
// Hidden: Quarantine Management
// const QuarantineManagement = LazyLoading(
//   lazy(async () => await import('@/pages/dashboard/organisation/compliance/quarantine-management'))
// )
const Audit = LazyLoading(lazy(async () => await import('@/pages/dashboard/organisation/compliance/audit')))
const AuditList = LazyLoading(
  lazy(async () => await import('@/components/dashboard/organisation/compliance/audit/audit-list'))
)

// settings
const Settings = LazyLoading(lazy(async () => await import('@/pages/dashboard/organisation/settings/settings')))
const OrganisationPreference = LazyLoading(
  lazy(async () => await import('@/pages/dashboard/organisation/organisation-preference'))
)
const RoleAndPermissionSetting = LazyLoading(
  lazy(async () => await import('@/pages/dashboard/organisation/settings/others/roles-and-permission.setting'))
)
const PolicyCategory = LazyLoading(
  lazy(async () => await import('@/pages/dashboard/organisation/settings/policy/policy-category'))
)
const DefaultPolicyReviewer = LazyLoading(
  lazy(async () => await import('@/pages/dashboard/organisation/settings/policy/default-policy-reviewer'))
)
const PolicyFrequencies = LazyLoading(
  lazy(async () => await import('@/pages/dashboard/organisation/settings/policy/policy-frequencies'))
)
const PolicyDashboardBenchmarks = LazyLoading(
  lazy(async () => await import('@/pages/dashboard/organisation/settings/policy/dashboard-benchmarks'))
)
const TaskStatus = LazyLoading(
  lazy(async () => await import('@/pages/dashboard/organisation/settings/task/task-status'))
)
const TaskSeverity = LazyLoading(
  lazy(async () => await import('@/pages/dashboard/organisation/settings/task/task-severity'))
)
const ServiceProviderContractStatus = LazyLoading(
  lazy(
    async () =>
      await import('@/pages/dashboard/organisation/settings/service-provider/service-provider-contract-status')
  )
)
const ServiceType = LazyLoading(
  lazy(async () => await import('@/pages/dashboard/organisation/settings/service-provider/service-type'))
)
const AssessmentTier = LazyLoading(
  lazy(async () => await import('@/pages/dashboard/organisation/settings/service-provider/assessment-tier'))
)
const AssessmentTierCriticality = LazyLoading(
  lazy(async () => await import('@/pages/dashboard/organisation/settings/service-provider/assessment-tier-criticality'))
)
const AssessmentTierSensitivity = LazyLoading(
  lazy(async () => await import('@/pages/dashboard/organisation/settings/service-provider/assessment-tier-sensitivity'))
)
const RiskTierMatrix = LazyLoading(
  lazy(async () => await import('@/pages/dashboard/organisation/settings/service-provider/risk-tier-matrix'))
)
const RiskReviewIntervals = LazyLoading(
  lazy(async () => await import('@/pages/dashboard/organisation/settings/service-provider/risk-review-intervals'))
)
const ControlMappingSettings = LazyLoading(
  lazy(async () => await import('@/pages/dashboard/organisation/settings/service-provider/control-mapping'))
)
const ScoringRubricSettings = LazyLoading(
  lazy(async () => await import('@/pages/dashboard/organisation/settings/service-provider/scoring-rubric'))
)
const VendorEvaluation = LazyLoading(
  lazy(async () => await import('@/pages/dashboard/organisation/service-provider/vendor-evaluation'))
)
const RiskLikelihood = LazyLoading(
  lazy(async () => await import('@/pages/dashboard/organisation/settings/risk/risk-likelihood'))
)
const RiskImpact = LazyLoading(
  lazy(async () => await import('@/pages/dashboard/organisation/settings/risk/risk-impact'))
)
const RiskRating = LazyLoading(
  lazy(async () => await import('@/pages/dashboard/organisation/settings/risk/risk-rating'))
)
const RiskMatrix = LazyLoading(
  lazy(async () => await import('@/pages/dashboard/organisation/settings/risk/risk-matrix'))
)
const RiskCategories = LazyLoading(
  lazy(async () => await import('@/pages/dashboard/organisation/settings/risk/risk-categories'))
)
const RiskTreatmentDecision = LazyLoading(
  lazy(async () => await import('@/pages/dashboard/organisation/settings/risk/risk-treatment-decision'))
)
const IncidentType = LazyLoading(
  lazy(async () => await import('@/pages/dashboard/organisation/settings/risk/incident-type'))
)
const Vulnerability = LazyLoading(
  lazy(async () => await import('@/pages/dashboard/organisation/settings/risk/vulnerability/vulnerability'))
)
const VulnerabilityDashboard = LazyLoading(
  lazy(async () => await import('@/pages/dashboard/organisation/settings/risk/vulnerability/vulnerability-dashboard'))
)
const ThreatCategory = LazyLoading(
  lazy(async () => await import('@/pages/dashboard/organisation/settings/risk/threat-category'))
)
const ThreatSource = LazyLoading(
  lazy(async () => await import('@/pages/dashboard/organisation/settings/risk/threat-source'))
)
const ThreatActor = LazyLoading(
  lazy(async () => await import('@/pages/dashboard/organisation/settings/risk/threat-actor'))
)
const Threat = LazyLoading(lazy(async () => await import('@/pages/dashboard/organisation/settings/risk/threat')))
const RiskIdentificationSource = LazyLoading(
  lazy(async () => await import('@/pages/dashboard/organisation/settings/risk/risk-identification-source'))
)
const RootIdentification = LazyLoading(
  lazy(async () => await import('@/pages/dashboard/organisation/settings/non-conformity/root-identification'))
)
const AffectedArea = LazyLoading(
  lazy(async () => await import('@/pages/dashboard/organisation/settings/non-conformity/affected-area'))
)
const Importance = LazyLoading(
  lazy(async () => await import('@/pages/dashboard/organisation/settings/non-conformity/importance'))
)
const ActionStatus = LazyLoading(
  lazy(async () => await import('@/pages/dashboard/organisation/settings/non-conformity/action-status'))
)
const AssetType = LazyLoading(
  lazy(async () => await import('@/pages/dashboard/organisation/settings/information-asset/asset-type'))
)
const ProjectList = LazyLoading(
  lazy(async () => await import('@/pages/dashboard/organisation/project-management/project'))
)
const TaskByProject = LazyLoading(
  lazy(async () => await import('@/pages/dashboard/organisation/project-management/task'))
)
const TaskDashboard = LazyLoading(
  lazy(async () => await import('@/pages/dashboard/organisation/project-management/task-dashboard'))
)
const MeetingMinutes = LazyLoading(
  lazy(async () => await import('@/pages/dashboard/organisation/meeting-minutes/meeting-minutes'))
)
const MeetingMinutesType = LazyLoading(
  lazy(async () => await import('@/pages/dashboard/organisation/meeting-minutes/meeting-minutes-type'))
)
const AiRegisterType = LazyLoading(
  lazy(async () => await import('@/pages/dashboard/organisation/ai-register/ai-register-type'))
)
const AiRegisterPermittedData = LazyLoading(
  lazy(async () => await import('@/pages/dashboard/organisation/ai-register/permitted-data'))
)
const AIScreeningQuestionnaire = LazyLoading(
  lazy(async () => await import('@/pages/dashboard/organisation/ai-register/ai-screening-questionnaire'))
)
const AIRegisterDashboard = LazyLoading(
  lazy(async () => await import('@/pages/dashboard/organisation/ai-register/ai-register-dashboard'))
)
const InformationSecurityObjectiveStatus = LazyLoading(
  lazy(async () => await import('@/pages/dashboard/organisation/information-security-objectives/status'))
)
const InformationSecurityObjectivePriority = LazyLoading(
  lazy(async () => await import('@/pages/dashboard/organisation/information-security-objectives/priority'))
)
const InformationSecurityObjectiveReportingFormat = LazyLoading(
  lazy(async () => await import('@/pages/dashboard/organisation/information-security-objectives/reporting-format'))
)
const InformationSecurityObjectiveAnalysisEvaluationFrequency = LazyLoading(
  lazy(
    async () =>
      await import('@/pages/dashboard/organisation/information-security-objectives/analysis-evaluation-frequency')
  )
)
const InformationSecurityObjectiveMonitoringFrequency = LazyLoading(
  lazy(async () => await import('@/pages/dashboard/organisation/information-security-objectives/monitoring-frequency'))
)
const InformationSecurityObjectiveMeasureType = LazyLoading(
  lazy(async () => await import('@/pages/dashboard/organisation/information-security-objectives/measure-type'))
)
const InformationSecurityObjectiveArea = LazyLoading(
  lazy(async () => await import('@/pages/dashboard/organisation/information-security-objectives/area'))
)
const InformationSecurityObjectivesList = LazyLoading(
  lazy(async () => await import('@/pages/dashboard/organisation/information-security-objectives/objectives'))
)
const AddInformationSecurityObjective = LazyLoading(
  lazy(async () => await import('@/pages/dashboard/organisation/information-security-objectives/add-objective'))
)

const IsmsAssuranceTaskPage = LazyLoading(
  lazy(async () => await import('@/pages/dashboard/organisation/isms-assurance-task'))
)

// Document Repo
const DocumentRepo = LazyLoading(
  lazy(async () => await import('@/pages/dashboard/organisation/document-repo/document-repo'))
)
const FolderPage = LazyLoading(lazy(async () => await import('@/pages/dashboard/organisation/document-repo/folder')))
const SubFolderPage = LazyLoading(
  lazy(async () => await import('@/pages/dashboard/organisation/document-repo/sub-folder'))
)
const StaffList = LazyLoading(lazy(async () => await import('@/pages/dashboard/organisation/staff-list')))
const OrganisationAIModel = LazyLoading(lazy(async () => await import('@/pages/dashboard/organisation/ai-model')))
const SharePointConfig = LazyLoading(lazy(async () => await import('@/pages/dashboard/organisation/sharepoint-config')))
const Department = LazyLoading(lazy(async () => await import('@/pages/dashboard/organisation/department')))
const ZapierWebhook = LazyLoading(lazy(async () => await import('@/pages/dashboard/organisation/zapier-webhook')))
const N8nWebhook = LazyLoading(lazy(async () => await import('@/pages/dashboard/organisation/n8n-webhook')))
const InformationAsset = LazyLoading(
  lazy(async () => await import('@/pages/dashboard/organisation/risk/information-asset/information-asset'))
)
const InformationAssetReport = LazyLoading(
  lazy(async () => await import('@/pages/dashboard/organisation/risk/information-asset/information-asset-report'))
)

// country regulatory register oos
const CountryRegulatoryRegister = LazyLoading(
  lazy(async () => await import('@/pages/dashboard/organisation/country-regulatory-register-oos/index'))
)

const CountryRegulatoryRegisterJurisdiction = LazyLoading(
  lazy(async () => await import('@/pages/dashboard/organisation/country-regulatory-register-oos/jurisdiction'))
)
const CountryRegulatoryRegisterOperationType = LazyLoading(
  lazy(async () => await import('@/pages/dashboard/organisation/country-regulatory-register-oos/operation-type'))
)
const CountryRegulatoryRegisterRegulatoryCategory = LazyLoading(
  lazy(async () => await import('@/pages/dashboard/organisation/country-regulatory-register-oos/regulatory-category'))
)
const CountryRegulatoryRegisterRegion = LazyLoading(
  lazy(async () => await import('@/pages/dashboard/organisation/country-regulatory-register-oos/region'))
)
const CountryRegulatoryRegisterRiskType = LazyLoading(
  lazy(async () => await import('@/pages/dashboard/organisation/country-regulatory-register-oos/risk-type'))
)
const ExemptionsRegister = LazyLoading(
  lazy(async () => await import('@/pages/dashboard/organisation/exemptions-register/exemptions-register'))
)
const OrganisationalContext = LazyLoading(
  lazy(async () => await import('@/pages/dashboard/organisation/organisational-context/organisational-context'))
)
const ChangeManagementProcess = LazyLoading(
  lazy(async () => await import('@/pages/dashboard/organisation/change-management-process/change-management-process'))
)
const ChangeManagementProcessCategory = LazyLoading(
  lazy(async () => await import('@/pages/dashboard/organisation/change-management-process/category'))
)
const UtilitySettings = LazyLoading(
  lazy(async () => await import('@/pages/dashboard/organisation/settings/others/utility'))
)
const FieldRequirements = LazyLoading(
  lazy(async () => await import('@/pages/dashboard/organisation/settings/field-requirements'))
)
const ToasterSettings = LazyLoading(
  lazy(async () => await import('@/pages/dashboard/organisation/settings/others/toaster-setting'))
)
const ExternalIntegrationSettings = LazyLoading(
  lazy(async () => await import('@/pages/dashboard/organisation/settings/others/external-integration'))
)
const ApiTokens = LazyLoading(lazy(async () => await import('@/pages/dashboard/organisation/api-key')))
const JiraIntegrationSettings = LazyLoading(
  lazy(async () => await import('@/pages/dashboard/organisation/settings/others/jira-integration'))
)
const AzureIntegrationSettings = LazyLoading(
  lazy(async () => await import('@/pages/dashboard/organisation/settings/others/azure-integration'))
)
const ConfluenceIntegrationSettings = LazyLoading(
  lazy(async () => await import('@/pages/dashboard/organisation/settings/others/confluence-integration'))
)
const GoogleDriveIntegrationSettings = LazyLoading(
  lazy(async () => await import('@/pages/dashboard/organisation/settings/others/google-drive-integration'))
)
const EmailConfigurationSettings = LazyLoading(
  lazy(async () => await import('@/pages/dashboard/organisation/settings/others/email-configuration'))
)
const EmailConfigurationCallback = LazyLoading(
  lazy(async () => await import('@/pages/dashboard/organisation/settings/others/email-configuration-callback'))
)
const GoogleDriveCallback = LazyLoading(
  lazy(async () => await import('@/pages/dashboard/organisation/settings/others/google-drive-callback'))
)
const PaloAltoNetworksIntegrationSettings = LazyLoading(
  lazy(async () => await import('@/pages/dashboard/organisation/settings/others/palo-alto-networks-integration'))
)
const MSDefenderIntegrationSettings = LazyLoading(
  lazy(async () => await import('@/pages/dashboard/organisation/settings/others/ms-defender-integration'))
)
const VantaIntegrationSettings = LazyLoading(
  lazy(async () => await import('@/pages/dashboard/organisation/settings/others/vanta-integration'))
)
const DrataIntegrationSettings = LazyLoading(
  lazy(async () => await import('@/pages/dashboard/organisation/settings/others/drata-integration'))
)
const MimecastIntegrationSettings = LazyLoading(
  lazy(async () => await import('@/pages/dashboard/organisation/settings/others/mimecast-integration'))
)
const PhishedDashboard = LazyLoading(
  lazy(async () => await import('@/pages/dashboard/organisation/security-awareness/phished/phished-dashboard'))
)
const MimecastDashboard = LazyLoading(
  lazy(async () => await import('@/pages/dashboard/organisation/security-awareness/mimecast/mimecast'))
)
const AIRegister = LazyLoading(lazy(async () => await import('@/pages/dashboard/organisation/ai-register/ai-register')))
const CredentialRegistry = LazyLoading(
  lazy(async () => await import('@/pages/dashboard/organisation/settings/pci/credential-registry/credential-registry'))
)
const PasswordPolicyPage = LazyLoading(
  lazy(async () => await import('@/pages/dashboard/organisation/settings/security/password-policy'))
)
const SignatureSettingsPage = LazyLoading(
  lazy(async () => await import('@/pages/dashboard/organisation/settings/security/signature-settings'))
)
const SsoSettingsPage = LazyLoading(
  lazy(async () => await import('@/pages/dashboard/organisation/settings/security/sso'))
)
const AssessmentRiskAssessment = LazyLoading(
  lazy(async () => await import('@/pages/dashboard/organisation/risk-assessment/assessment'))
)
const AssessmentPeopleRiskAssessment = LazyLoading(
  lazy(async () => await import('@/pages/dashboard/organisation/people-risk-assessment/assessment'))
)
const OrganisationAITokenUsage = LazyLoading(
  lazy(async () => await import('@/pages/dashboard/organisation/ai-token-usage'))
)
const ActivityAnalytics = LazyLoading(
  lazy(async () => await import('@/pages/dashboard/organisation/activity-analytics'))
)
const OrganisationEmailLogs = LazyLoading(lazy(async () => await import('@/pages/dashboard/organisation/mail-logs')))
const ClassificationLabel = LazyLoading(
  lazy(async () => await import('@/pages/dashboard/organisation/classification-label'))
)
const PdfTemplateSettingsPage = LazyLoading(
  lazy(async () => await import('@/pages/dashboard/organisation/settings/pdf-template'))
)
// CTRA (Cyber Threat Risk Assessment)
const CTRAPrograms = LazyLoading(lazy(async () => await import('@/pages/dashboard/organisation/ctra/programs')))
const CTRAZones = LazyLoading(lazy(async () => await import('@/pages/dashboard/organisation/ctra/zone')))
const CTRAThreatScenarioCatalog = LazyLoading(
  lazy(async () => await import('@/pages/dashboard/organisation/ctra/threat-scenario'))
)
const CTRAEngagementList = LazyLoading(
  lazy(async () => await import('@/pages/dashboard/organisation/ctra/engagement/engagement'))
)
const ManageCTRASoA = LazyLoading(lazy(async () => await import('@/pages/dashboard/organisation/ctra/manage-soa')))
const AddCTRAEngagement = LazyLoading(
  lazy(async () => await import('@/pages/dashboard/organisation/ctra/engagement/add-engagement'))
)
const CTRAEngagementOverview = LazyLoading(
  lazy(async () => await import('@/pages/dashboard/organisation/ctra/engagement/engagement-overview'))
)
const PromoteCTRARisks = LazyLoading(
  lazy(async () => await import('@/pages/dashboard/organisation/ctra/engagement/promote-risks'))
)
const AddCTRARisk = LazyLoading(
  lazy(async () => await import('@/pages/dashboard/organisation/ctra/engagement/add-risk'))
)
const CTRAQuestionnaireBatchWizard = LazyLoading(
  lazy(async () => await import('@/pages/dashboard/organisation/ctra/engagement/questionnaire-batch'))
)

// ORGANISATION LEVEL - AI SERVICES ROUTES
const StandardQA = LazyLoading(lazy(async () => await import('@/pages/dashboard/organisation/ai/standard-qa')))
const AiChanges = LazyLoading(lazy(async () => await import('@/pages/dashboard/organisation/ai/ai-changes')))

// ========================================
// SUPER ADMIN ROUTES
// ========================================
const SignupRequest = LazyLoading(lazy(async () => await import('@/pages/dashboard/superadmin/signup-request')))
const SignupRequestReport = LazyLoading(
  lazy(async () => await import('@/components/dashboard/superadmin/signup/signup-report'))
)
const AdminManageBugReport = LazyLoading(
  lazy(async () => await import('@/pages/dashboard/superadmin/manage-bug-report'))
)
const DashboardDefaults = LazyLoading(lazy(async () => await import('@/pages/dashboard/superadmin/dashboard-defaults')))
const AdminManageOrganisation = LazyLoading(
  lazy(async () => await import('@/pages/dashboard/superadmin/organisation/organisation'))
)
const AdminManageChildOrganisation = LazyLoading(
  lazy(async () => await import('@/pages/dashboard/superadmin/organisation/child-organisation'))
)
const AdminManageOrganisationUsers = LazyLoading(
  lazy(async () => await import('@/pages/dashboard/superadmin/organisation/organisation-users'))
)
const ClerkUsers = LazyLoading(lazy(async () => await import('@/pages/dashboard/superadmin/clerk-users')))
const Assessment = LazyLoading(lazy(async () => await import('@/pages/dashboard/superadmin/assessment')))
const AdminManageAdmin = LazyLoading(lazy(async () => await import('@/pages/dashboard/superadmin/admin-list')))
const AdminManageResource = LazyLoading(lazy(async () => await import('@/pages/dashboard/superadmin/resource')))
const AdminUserManual = LazyLoading(lazy(async () => await import('@/pages/dashboard/superadmin/user-manual')))
const AdminMCPServerExtension = LazyLoading(
  lazy(async () => await import('@/pages/dashboard/superadmin/mcp-server-extension'))
)
const HelpfulTips = LazyLoading(lazy(async () => await import('@/pages/dashboard/superadmin/helpful-tips')))
const UserGuides = LazyLoading(lazy(async () => await import('@/pages/dashboard/superadmin/user-guides')))
const UserGuideEditor = LazyLoading(lazy(async () => await import('@/pages/dashboard/superadmin/user-guide-editor')))
const UserGuideApiKeys = LazyLoading(lazy(async () => await import('@/pages/dashboard/superadmin/user-guide-api-keys')))
const ITOperations = LazyLoading(lazy(async () => await import('@/pages/dashboard/superadmin/it-operations')))
const ServerLogs = LazyLoading(lazy(async () => await import('@/pages/dashboard/superadmin/server-logs')))
const ReleaseNote = LazyLoading(lazy(async () => await import('@/pages/dashboard/superadmin/release-note')))
const ApiDocsTokens = LazyLoading(lazy(async () => await import('@/pages/dashboard/superadmin/api-docs-tokens')))
const AdminManageStandards = LazyLoading(lazy(async () => await import('@/pages/dashboard/superadmin/standards')))
const AdminManageSAQTypes = LazyLoading(lazy(async () => await import('@/pages/dashboard/superadmin/saq-types')))
const AdminManageFiscalYears = LazyLoading(lazy(async () => await import('@/pages/dashboard/superadmin/fiscal-year')))
const AdminManageStandardLevel = LazyLoading(
  lazy(async () => await import('@/pages/dashboard/superadmin/standard-level'))
)
const AdminManageControlGroup = LazyLoading(
  lazy(async () => await import('@/pages/dashboard/superadmin/control-group'))
)
const AdminManageControlMapping = LazyLoading(
  lazy(async () => await import('@/pages/dashboard/superadmin/control-mapping'))
)
const AdminVendorBaseline = LazyLoading(lazy(async () => await import('@/pages/dashboard/superadmin/vendor-baseline')))
const AdminManageControls = LazyLoading(lazy(async () => await import('@/pages/dashboard/superadmin/controls')))
const AdminManageControlQuestionnaires = LazyLoading(
  lazy(async () => await import('@/pages/dashboard/superadmin/control-questionnaire'))
)
const AdminManageMaturityModels = LazyLoading(
  lazy(async () => await import('@/pages/dashboard/superadmin/maturity-models'))
)
const AdminManageMaturityLevels = LazyLoading(
  lazy(async () => await import('@/pages/dashboard/superadmin/maturity-levels'))
)
const Module = LazyLoading(lazy(async () => await import('@/pages/dashboard/superadmin/module')))
const PciDssTask = LazyLoading(lazy(async () => await import('@/pages/dashboard/superadmin/pci-dss-task')))
const PciDssEvidencePackTask = LazyLoading(
  lazy(async () => await import('@/pages/dashboard/superadmin/pci-dss-evidence-pack-task'))
)
const Subscription = LazyLoading(
  lazy(async () => await import('@/pages/dashboard/superadmin/subscription/subscription'))
)
const SubscriptionType = LazyLoading(
  lazy(async () => await import('@/pages/dashboard/superadmin/subscription/subscription-type'))
)
const FCMTokens = LazyLoading(lazy(async () => await import('@/pages/dashboard/superadmin/fcm-token')))

const AllOrganisationActivityLogs = LazyLoading(
  lazy(async () => await import('@/pages/dashboard/superadmin/all-organisation-activity-logs'))
)
const SuperAdminActivityAnalytics = LazyLoading(
  lazy(async () => await import('@/pages/dashboard/superadmin/activity-analytics'))
)
// System Risk Assessment Tools
const RiskAssessmentSystemExposure = LazyLoading(
  lazy(async () => await import('@/pages/dashboard/superadmin/risk-assessment/system-exposure'))
)
const RiskAssessmentHostingModel = LazyLoading(
  lazy(async () => await import('@/pages/dashboard/superadmin/risk-assessment/hosting-model'))
)
const RiskAssessmentDataClassification = LazyLoading(
  lazy(async () => await import('@/pages/dashboard/superadmin/risk-assessment/data-classification'))
)
const RiskAssessmentUserBase = LazyLoading(
  lazy(async () => await import('@/pages/dashboard/superadmin/risk-assessment/user-base'))
)
const RiskAssessmentType = LazyLoading(
  lazy(async () => await import('@/pages/dashboard/superadmin/risk-assessment/assessment-type'))
)
const RiskAssessmentQuestionnaire = LazyLoading(
  lazy(async () => await import('@/pages/dashboard/superadmin/risk-assessment/questionnaire/manage-questionnaire'))
)
const RiskAssessmentQuestionnaireBuilder = LazyLoading(
  lazy(async () => await import('@/pages/dashboard/superadmin/risk-assessment/questionnaire/questionnaire-builder'))
)
const PeopleRiskAssessmentType = LazyLoading(
  lazy(async () => await import('@/pages/dashboard/superadmin/people-risk-assessment/assessment-type'))
)
const PeopleRiskAssessmentQuestionnaire = LazyLoading(
  lazy(
    async () => await import('@/pages/dashboard/superadmin/people-risk-assessment/questionnaire/manage-questionnaire')
  )
)
const PeopleRiskAssessmentQuestionnaireBuilder = LazyLoading(
  lazy(
    async () => await import('@/pages/dashboard/superadmin/people-risk-assessment/questionnaire/questionnaire-builder')
  )
)
const AIModels = LazyLoading(lazy(async () => await import('@/pages/dashboard/superadmin/ai-models')))
const AIPromptCatalog = LazyLoading(lazy(async () => await import('@/pages/dashboard/superadmin/ai-prompt-catalog')))
const PhishedRecipientTagColor = LazyLoading(
  lazy(async () => await import('@/pages/dashboard/superadmin/phished-recipient-tag-color'))
)
const EmailTemplate = LazyLoading(lazy(async () => await import('@/pages/dashboard/superadmin/email-template')))
const SuperAdminEmailLogs = LazyLoading(lazy(async () => await import('@/pages/dashboard/superadmin/mail-logs')))
const CommonPdfTemplatePage = LazyLoading(
  lazy(async () => await import('@/pages/dashboard/superadmin/common-pdf-template'))
)
const AITokenUsage = LazyLoading(lazy(async () => await import('@/pages/dashboard/superadmin/ai-token-usage')))
const AssistantChatHistory = LazyLoading(
  lazy(async () => await import('@/pages/dashboard/superadmin/assistant-chat-history'))
)
const SchedulerConfig = LazyLoading(lazy(async () => await import('@/pages/dashboard/superadmin/scheduler-config')))
const AiDatabase = LazyLoading(lazy(async () => await import('@/pages/dashboard/superadmin/database/ai-database')))
const DatabasePage = LazyLoading(lazy(async () => await import('@/pages/dashboard/superadmin/database/database')))

// SUPER ADMIN LEVEL - AI SERVICES ROUTES
const ControlEmbeddings = LazyLoading(
  lazy(async () => await import('@/pages/dashboard/superadmin/ai-rag/control-embeddings'))
)
const EvidenceIndex = LazyLoading(lazy(async () => await import('@/pages/dashboard/superadmin/ai-rag/evidence-index')))
const ControlMapping = LazyLoading(
  lazy(async () => await import('@/pages/dashboard/superadmin/ai-rag/control-mapping'))
)
const AiAgents = LazyLoading(lazy(async () => await import('@/pages/dashboard/superadmin/ai-rag/agents')))
const AiPolicyTemplates = LazyLoading(
  lazy(async () => await import('@/pages/dashboard/superadmin/ai-rag/policy-templates'))
)
const AiPolicyPacks = LazyLoading(lazy(async () => await import('@/pages/dashboard/superadmin/ai-rag/policy-packs')))
const AiActivityLog = LazyLoading(lazy(async () => await import('@/pages/dashboard/superadmin/ai-rag/activity-log')))
const AiServiceTokenUsage = LazyLoading(
  lazy(async () => await import('@/pages/dashboard/superadmin/ai-rag/token-usage'))
)
const AiServiceStatus = LazyLoading(
  lazy(async () => await import('@/pages/dashboard/superadmin/ai-rag/service-status'))
)

// ========================================
// VENDOR (SERVICE PROVIDER PORTAL) ROUTES
// ========================================
const VendorDashboard = LazyLoading(lazy(async () => await import('@/pages/dashboard/vendor/vendor-dashboard')))
const VendorAssessmentList = LazyLoading(
  lazy(async () => await import('@/pages/dashboard/vendor/vendor-assessment-list'))
)
const VendorAssessmentDetail = LazyLoading(
  lazy(async () => await import('@/pages/dashboard/vendor/vendor-assessment-detail'))
)
const VendorCTRAAssessmentDetail = LazyLoading(
  lazy(async () => await import('@/pages/dashboard/vendor/vendor-ctra-assessment-detail'))
)

export const VendorRoute: IRoute[] = [
  {
    path: '/',
    element: <Outlet />,
    role: [Role.VENDOR, Role.VENDOR_COLLABORATOR],
    children: [
      {
        path: '/',
        element: <VendorDashboard />,
      },
      {
        path: '/vendor/assessments',
        element: <VendorAssessmentList />,
      },
      {
        path: '/vendor/assessments/:id',
        element: <VendorAssessmentDetail />,
      },
      {
        path: '/vendor/ctra-assessments/:id',
        element: <VendorCTRAAssessmentDetail />,
      },
      {
        path: '/profile',
        element: <Profile />,
      },
      {
        path: '/settings/profile',
        element: <Profile />,
      },
      {
        path: '/settings/mfa',
        element: <MfaSetupPage />,
      },
      {
        path: '/update-password',
        element: <UpdatePassword />,
      },
      {
        path: '*',
        element: <PageNotFound />,
      },
    ],
  },
]

export const OrgRoute: IRoute[] = [
  {
    path: '/',
    element: <Outlet />,
    children: [
      // ========================================
      // COMMON ROUTES
      // ========================================
      {
        path: '/',
        element: <DashboardPage />,
      },
      {
        path: '/login-logs',
        element: <LoginLogs />,
      },
      {
        path: '/activity-logs',
        element: <ActivityLogs />,
      },
      {
        path: '/activity-logs/all-users',
        role: [Role.ORG_ADMIN, Role.ORG_MANAGER],
        feature: Feature.USERS,
        element: <ShowAllUserActivityLogs />,
      },
      {
        path: '/activity-analytics',
        role: [Role.ORG_ADMIN, Role.ORG_MANAGER],
        feature: Feature.USERS,
        element: <ActivityAnalytics />,
      },
      {
        path: '/email-logs',
        element: <OrganisationEmailLogs />,
      },
      {
        path: '/notifications',
        element: <Notifications />,
      },
      {
        path: '/profile',
        element: <Profile />,
      },
      {
        path: '/settings/profile',
        element: <Profile />,
      },
      {
        path: '/settings/mfa',
        element: <MfaSetupPage />,
      },
      {
        path: '/update-password',
        element: <UpdatePassword />,
      },

      // ========================================
      // ORGANISATION ROUTES
      // ========================================
      {
        path: '/policy',
        element: <Policy />,
        feature: Feature.POLICY,
      },
      {
        path: '/policy-dashboard',
        element: <PolicyDashboard />,
        feature: Feature.POLICY,
      },
      {
        path: '/manage-users',
        feature: Feature.USERS,
        element: <ManageUserPage />,
      },
      {
        path: '/manage-organisation',
        element: <ManageOrganisation />,
        role: [Role.ORG_ADMIN, Role.ORG_MANAGER],
        feature: Feature.SUB_ORGANISATION,
      },
      {
        path: '/on-boarding',
        element: <Navigate to='/' />,
        role: [Role.ORG_ADMIN],
      },
      {
        path: '/on-boarding/:id',
        element: <Onboarding />,
        role: [Role.ORG_ADMIN],
      },
      {
        path: '/document-repo',
        element: <DocumentRepo />,
        feature: Feature.DOCUMENT_REPOSITORY,
      },
      {
        path: '/document-repo/:id/folder',
        element: <FolderPage />,
        feature: Feature.DOCUMENT_REPOSITORY,
      },
      {
        path: '/document-repo/:id/folder/:folderId/sub-folder',
        element: <SubFolderPage />,
        feature: Feature.DOCUMENT_REPOSITORY,
      },
      {
        path: '/manage-projects',
        element: <ProjectList />,
        feature: Feature.PROJECT,
      },
      {
        path: '/manage-projects/tasks',
        element: <TaskByProject />,
        feature: Feature.PROJECT,
      },
      {
        path: '/tasks-dashboard',
        element: <TaskDashboard />,
        feature: Feature.PROJECT,
      },
      {
        path: '/meeting-minutes',
        element: <MeetingMinutes />,
        feature: Feature.MEETING_MINUTES,
      },
      {
        path: '/meeting-minutes-type',
        element: <MeetingMinutesType />,
        feature: Feature.MEETING_MINUTES,
      },
      {
        path: '/manage-staff',
        element: <StaffList />,
        feature: Feature.STAFF,
      },
      {
        path: '/organisation/ai-model',
        element: <OrganisationAIModel />,
        role: [Role.ORG_ADMIN, Role.ORG_MANAGER],
        feature: Feature.AI_MODEL,
      },
      {
        path: '/organisation/sharepoint-config',
        element: <SharePointConfig />,
        role: [Role.ORG_ADMIN, Role.ORG_MANAGER],
        feature: Feature.SHAREPOINT_CONFIG,
      },
      {
        path: '/organisation/ai-token-usage',
        element: <OrganisationAITokenUsage />,
        role: [Role.ORG_ADMIN, Role.ORG_MANAGER],
      },
      {
        path: '/organisation/ai-changes',
        element: <AiChanges />,
        feature: Feature.POLICY,
        role: [Role.ORG_ADMIN, Role.ORG_MANAGER],
      },
      {
        path: '/manage-department',
        element: <Department />,
        feature: Feature.STAFF,
      },
      {
        path: '/settings/zapier-webhook',
        element: <ZapierWebhook />,
        feature: Feature.ZAPIER,
      },
      {
        path: '/settings/n8n-webhook',
        element: <N8nWebhook />,
        feature: Feature.N8N,
      },
      {
        path: '/information-asset-report',
        element: <InformationAssetReport />,
        feature: Feature.INFORMATION_ASSET,
      },
      {
        path: '/information-asset',
        element: <InformationAsset />,
        feature: Feature.INFORMATION_ASSET,
      },
      {
        path: '/risk-list',
        element: <RiskList />,
        feature: Feature.RISK,
      },
      {
        path: '/information-security-objectives',
        element: <InformationSecurityObjectivesList />,
        feature: Feature.INFORMATION_SECURITY_OBJECTIVE,
      },
      {
        path: '/isms-assurance-calendar',
        element: <IsmsAssuranceTaskPage />,
        feature: Feature.ISMS_ASSURANCE_CALENDAR,
      },
      {
        path: '/add-information-security-objective',
        element: <AddInformationSecurityObjective />,
        feature: Feature.INFORMATION_SECURITY_OBJECTIVE,
      },
      {
        path: '/add-information-security-objective/:id',
        element: <AddInformationSecurityObjective />,
        feature: Feature.INFORMATION_SECURITY_OBJECTIVE,
      },
      {
        path: '/add-risk',
        element: <AddRisk />,
        feature: Feature.RISK,
      },
      {
        path: '/add-risk/:id',
        element: <AddRisk />,
        feature: Feature.RISK,
      },
      {
        path: '/plan-of-action',
        element: <PlanOfAction />,
        feature: Feature.RISK,
      },
      {
        path: '/risk-dashboard',
        element: <RiskDashboard />,
        feature: Feature.RISK,
      },
      {
        // access; accept/reject decisions are gated to ORG_ADMIN in-component.
        path: '/risk/escalations',
        element: <RiskEscalation />,
        feature: Feature.RISK,
      },
      {
        // RISK view access; generate is gated to ORG_ADMIN/ORG_RISK_MANAGER in-component.
        path: '/risk/residual-risk-report',
        element: <ResidualRiskReport />,
        feature: Feature.RISK,
      },
      {
        path: '/risk/vulnerability',
        element: <Vulnerability />,
        role: [Role.ORG_ADMIN, Role.ORG_MANAGER],
        feature: Feature.RISK,
      },
      {
        path: '/risk/vulnerability/dashboard',
        element: <VulnerabilityDashboard />,
        role: [Role.ORG_ADMIN, Role.ORG_MANAGER],
        feature: Feature.RISK,
      },
      {
        path: '/security-awareness/phished',
        element: <PhishedDashboard />,
        feature: Feature.PHISHED,
      },
      {
        path: '/security-awareness/mimecast',
        element: <MimecastDashboard />,
        feature: Feature.MIMECAST,
      },
      {
        path: '/ai-register',
        element: <AIRegister />,
        feature: Feature.AI_REGISTER,
      },
      {
        path: '/incident-dashboard',
        element: <IncidentDashboard />,
        feature: Feature.ASSESSMENTS,
      },
      {
        path: '/assessments',
        element: <AssessmentCards />,
        feature: Feature.ASSESSMENTS,
      },
      {
        path: '/archive-assessment',
        element: <ArchiveAssessment />,
        feature: Feature.ASSESSMENTS,
        role: [Role.ORG_ADMIN],
      },
      {
        path: '/compliance-report',
        element: <ComplianceReport />,
        feature: Feature.ASSESSMENTS,
      },
      {
        path: '/assessments/start-assessments/:organisationStandardId',
        element: <StartAssessment />,
        feature: Feature.ASSESSMENTS,
      },
      {
        path: '/assessments/start-ism-gap-assessment/:organisationStandardId',
        element: <StartIsmGapAssessment />,
        feature: Feature.ASSESSMENTS,
      },
      {
        path: '/assessments/start-cis-assessment/:organisationStandardId',
        element: <StartCisAssessment />,
        feature: Feature.ASSESSMENTS,
      },
      {
        path: '/assessments/start-soc2-assessment/:organisationStandardId',
        element: <StartSoc2Assessment />,
        feature: Feature.ASSESSMENTS,
      },
      {
        path: '/assessments/start-csp-assessment/:organisationStandardId',
        element: <StartCspAssessment />,
        feature: Feature.ASSESSMENTS,
      },
      {
        path: '/assessments/start-pci-assessment/:organisationStandardId',
        element: <StartPciAssessment />,
        feature: Feature.ASSESSMENTS,
      },
      {
        path: '/assessments/start-pci-roc-assessment/:organisationStandardId',
        element: <StartPciRocAssessment />,
        feature: Feature.ASSESSMENTS,
      },
      {
        path: '/assessments/start-pci-saq-assessment/:organisationStandardId',
        element: <StartPciSAQAssessment />,
        feature: Feature.ASSESSMENTS,
      },
      {
        path: '/assessments/saq-report/:organisationStandardId',
        element: <SaqReport />,
        feature: Feature.ASSESSMENTS,
      },
      {
        path: '/assessments/pci-scoping-configuration/:organisationStandardId',
        element: <PciScopingConfiguration />,
        feature: Feature.ASSESSMENTS,
      },
      {
        path: '/assessments/ism-scoping-configuration/:organisationStandardId/:scopingId',
        element: <IsmScopingConfiguration />,
        role: [
          Role.ORG_ADMIN,
          Role.ORG_MANAGER,
          Role.ORG_ASSESSOR,
          Role.ORG_VIEWER,
          Role.ORG_CONSULTANT,
          Role.ORG_AUDITOR,
        ],
        feature: Feature.ASSESSMENTS,
      },
      {
        path: '/assessments/ism-scoping-configuration/:organisationStandardId',
        element: <IsmScopingConfiguration />,
        role: [
          Role.ORG_ADMIN,
          Role.ORG_MANAGER,
          Role.ORG_ASSESSOR,
          Role.ORG_VIEWER,
          Role.ORG_CONSULTANT,
          Role.ORG_AUDITOR,
        ],
        feature: Feature.ASSESSMENTS,
      },
      {
        path: '/assessments/pci-scoping-configuration/:organisationStandardId/:scopingId',
        element: <PciScopingConfiguration />,
        feature: Feature.ASSESSMENTS,
      },
      {
        path: '/assessments/start-ism-irap-assessment/:organisationStandardId',
        element: <StartIsmIrapAssessment />,
        feature: Feature.ASSESSMENTS,
      },
      {
        path: '/assessments/:organisationStandardId',
        element: <AssessmentDetail />,
        feature: Feature.ASSESSMENTS,
      },
      {
        path: '/archive-assessment/:organisationStandardId',
        element: <AssessmentDetail />,
        feature: Feature.ASSESSMENTS,
      },
      {
        path: '/assessments/csp-assessment/dashboard/:organisationStandardId',
        element: <CspDashboard />,
        feature: Feature.ASSESSMENTS,
      },
      {
        path: '/assessments/csp-assessment/:organisationStandardId',
        element: <CspGAPAssessmentDetail />,
        feature: Feature.ASSESSMENTS,
      },
      {
        path: '/archive-assessment/csp-assessment/:organisationStandardId',
        element: <CspGAPAssessmentDetail />,
        feature: Feature.ASSESSMENTS,
      },
      {
        path: '/assessments/csp-assessment/review/:organisationStandardId/*',
        element: <CspAssessmentReview />,
        feature: Feature.ASSESSMENTS,
      },
      {
        path: '/assessments/csp-assessment/attestation/:organisationStandardId/*',
        element: <CspAssessmentAttestation />,
        feature: Feature.ASSESSMENTS,
      },
      {
        path: '/assessments/csp-assessment/exception-request/:organisationStandardId/*',
        element: <CspAssessmentExceptionRequest />,
        feature: Feature.ASSESSMENTS,
      },
      {
        path: '/assessments/csp-assessment/reporting-calendar/:organisationStandardId',
        element: <CspReportingCalendar />,
        feature: Feature.ASSESSMENTS,
      },
      {
        path: '/assessments/pci-assessment/:organisationStandardId',
        element: <PciAssessmentDetail />,
        feature: Feature.ASSESSMENTS,
      },
      {
        path: '/archive-assessment/pci-assessment/:organisationStandardId',
        element: <PciAssessmentDetail />,
        feature: Feature.ASSESSMENTS,
      },
      {
        path: '/assessments/pci-saq-assessment/:organisationStandardId',
        element: <PciSAQAssessmentDetail />,
        feature: Feature.ASSESSMENTS,
      },
      {
        path: '/archive-assessment/pci-saq-assessment/:organisationStandardId',
        element: <PciSAQAssessmentDetail />,
        feature: Feature.ASSESSMENTS,
      },
      {
        path: '/assessments/ism-irap-assessment/:organisationStandardId',
        element: <IsmIrapAssessmentDetail />,
        feature: Feature.ASSESSMENTS,
      },
      {
        path: '/archive-assessment/ism-irap-assessment/:organisationStandardId',
        element: <IsmIrapAssessmentDetail />,
        feature: Feature.ASSESSMENTS,
      },
      {
        path: '/assessments/ism-principle-assessment/:organisationStandardId',
        element: <IsmPrincipleAssessmentDetail />,
        feature: Feature.ASSESSMENTS,
      },
      {
        path: '/reporting/:organisationStandardId',
        element: <Reporting />,
        feature: Feature.ASSESSMENTS,
      },
      {
        path: '/assessments/logs/:organisationStandardId',
        element: <AssessmentsLogs />,
        feature: Feature.ASSESSMENTS,
      },
      {
        path: '/non-conformity/:organisationStandardId',
        element: <NonConformityDetail />,
        feature: Feature.ASSESSMENTS,
      },
      {
        path: '/issues',
        element: <Navigate to={`/issues/${ISSUES_TAB.NON_CONFORMITIES}`} replace />,
        feature: Feature.ASSESSMENTS,
      },
      {
        path: '/issues/:tab',
        element: <IssuesPage />,
        feature: Feature.ASSESSMENTS,
      },
      {
        path: '/opportunity-for-improvement',
        element: <LegacyIssuesRedirect tab={ISSUES_TAB.OPPORTUNITIES_FOR_IMPROVEMENT} />,
        feature: Feature.ASSESSMENTS,
      },
      {
        path: '/non-conformity',
        element: <LegacyIssuesRedirect tab={ISSUES_TAB.NON_CONFORMITIES} />,
        feature: Feature.ASSESSMENTS,
      },
      {
        path: '/statement-of-applicability',
        element: <StatementOfApplicabilityPage />,
        feature: Feature.ASSESSMENTS,
      },
      {
        path: '/audit-list/:organisationStandardId',
        element: <AuditList />,
        role: [Role.ORG_ADMIN, Role.ORG_MANAGER, Role.ORG_AUDITOR],
        feature: Feature.ASSESSMENTS,
      },
      {
        path: '/audit/:organisationStandardId',
        element: <Audit />,
        role: [Role.ORG_AUDITOR],
        feature: Feature.ASSESSMENTS,
      },
      {
        path: '/report-library',
        element: <ReportLibrary />,
        feature: Feature.REPORT_LIBRARY,
      },
      // Hidden: Quarantine Management
      // {
      //   path: '/quarantine-management',
      //   element: <QuarantineManagement />,
      //   role: [Role.ORG_ADMIN, Role.ORG_MANAGER],
      //   feature: Feature.ASSESSMENTS,
      // },
      {
        path: '/incident',
        element: <IncidentList />,
        feature: Feature.INCIDENT_REGISTER,
      },
      {
        path: '/add-incident',
        element: <AddIncident />,
        feature: Feature.INCIDENT_REGISTER,
      },
      {
        path: '/add-incident/:id',
        element: <AddIncident />,
        feature: Feature.INCIDENT_REGISTER,
      },
      {
        path: '/service-provider-dashboard',
        element: <ServiceProviderDashboard />,
        feature: Feature.SERVICE_PROVIDER,
      },
      {
        path: '/service-provider-list',
        element: <ServiceProviderList />,
        feature: Feature.SERVICE_PROVIDER,
      },
      {
        path: '/service-provider-incoming-questionnaire',
        element: <IncomingQuestionnaireList />,
        feature: Feature.SERVICE_PROVIDER,
      },
      {
        path: '/service-provider-report-library',
        element: <ServiceProviderReportLibrary />,
        feature: Feature.SERVICE_PROVIDER,
      },
      {
        path: '/service-provider-ai-risk/:id/edit',
        element: <AiRiskStepperPage />,
        feature: Feature.SERVICE_PROVIDER,
      },
      {
        path: '/service-provider-incoming-questionnaire/:id',
        element: <IncomingQuestionnaireDetail />,
        feature: Feature.SERVICE_PROVIDER,
      },
      {
        path: '/service-provider-incoming-questionnaire/:id/answer',
        element: <AnswerIncomingQuestionnaire />,
        feature: Feature.SERVICE_PROVIDER,
      },
      {
        path: '/add-service-provider',
        element: <AddServiceProvider />,
        feature: Feature.SERVICE_PROVIDER,
      },
      {
        path: '/add-service-provider/:id',
        element: <AddServiceProvider />,
        feature: Feature.SERVICE_PROVIDER,
      },
      {
        path: '/service-provider/:id/impact-assessment',
        element: <ServiceProviderImpactAssessment />,
        feature: Feature.SERVICE_PROVIDER,
      },
      {
        path: '/service-provider/:id/risk-posture',
        element: <ServiceProviderRiskPosture />,
        feature: Feature.SERVICE_PROVIDER,
      },
      {
        path: '/assessment-library',
        element: <AssessmentLibrary />,
        feature: Feature.SERVICE_PROVIDER,
      },
      {
        path: '/add-assessment-library',
        element: <AddAssessmentLibrary />,
        feature: Feature.SERVICE_PROVIDER,
      },
      {
        path: '/add-assessment-library/:id',
        element: <AddAssessmentLibrary />,
        feature: Feature.SERVICE_PROVIDER,
      },
      {
        path: '/service-provider-assessment',
        element: <ServiceProviderAssessment />,
        feature: Feature.SERVICE_PROVIDER,
      },
      {
        path: '/add-service-provider-assessment',
        element: <AddServiceProviderAssessment />,
        feature: Feature.SERVICE_PROVIDER,
      },
      {
        path: '/add-service-provider-assessment/:id',
        element: <AddServiceProviderAssessment />,
        feature: Feature.SERVICE_PROVIDER,
      },
      {
        path: '/assessment-library/questionnaire-builder/:id',
        element: <QuestionnaireBuilder />,
        feature: Feature.SERVICE_PROVIDER,
      },
      {
        path: '/organisation/resource',
        element: <ResourceHelpDesk />,
        feature: Feature.ASSESSMENTS,
      },

      // Country Regulatory Register OOS
      {
        path: '/country-regulatory-register-oos',
        element: <CountryRegulatoryRegister />,
        feature: Feature.COUNTRY_REGULATORY_REGISTER,
      },

      {
        path: '/country-regulatory-register-oos/jurisdiction',
        element: <CountryRegulatoryRegisterJurisdiction />,
        feature: Feature.COUNTRY_REGULATORY_REGISTER,
      },
      {
        path: '/country-regulatory-register-oos/operation-type',
        element: <CountryRegulatoryRegisterOperationType />,
        feature: Feature.COUNTRY_REGULATORY_REGISTER,
      },
      {
        path: '/country-regulatory-register-oos/regulatory-category',
        element: <CountryRegulatoryRegisterRegulatoryCategory />,
        feature: Feature.COUNTRY_REGULATORY_REGISTER,
      },
      {
        path: '/country-regulatory-register-oos/region',
        element: <CountryRegulatoryRegisterRegion />,
        feature: Feature.COUNTRY_REGULATORY_REGISTER,
      },
      {
        path: '/country-regulatory-register-oos/risk-type',
        element: <CountryRegulatoryRegisterRiskType />,
        feature: Feature.COUNTRY_REGULATORY_REGISTER,
      },
      {
        path: '/exemptions-register',
        element: <ExemptionsRegister />,
        feature: Feature.EXEMPTIONS_REGISTER,
      },
      {
        path: '/organisational-context',
        element: <OrganisationalContext />,
        feature: Feature.ORGANISATIONAL_CONTEXT,
      },
      {
        path: '/change-management-process',
        element: <ChangeManagementProcess />,
        feature: Feature.CHANGE_MANAGEMENT_PROCESS,
      },
      {
        path: '/settings/change-management-process-category',
        element: <ChangeManagementProcessCategory />,
        feature: Feature.CHANGE_MANAGEMENT_PROCESS,
      },
      // {
      //   path: '/release-notes',
      //   element: <OrganisationReleaseNote />,
      // },
      {
        path: '/bug-report',
        element: <OrganisationUserManageBugReport />,
      },
      {
        path: '/risk-assessment',
        element: <AssessmentRiskAssessment />,
        feature: Feature.RISK_ASSESSMENT,
      },
      {
        path: '/people-risk-assessment',
        element: <AssessmentPeopleRiskAssessment />,
        feature: Feature.PEOPLE_RISK_ASSESSMENT,
      },
      // ========================================
      // START SETTINGS ROUTES (ORGANISATION)
      // ========================================
      {
        path: '/settings',
        element: <Settings />,
        role: [Role.ORG_ADMIN, Role.ORG_MANAGER],
      },
      // Policy Settings
      {
        path: '/settings/policy/policy-category',
        element: <PolicyCategory />,
        role: [Role.ORG_ADMIN, Role.ORG_MANAGER],
        feature: Feature.POLICY,
      },
      {
        path: '/settings/policy/default-policy-reviewer',
        element: <DefaultPolicyReviewer />,
        role: [Role.ORG_ADMIN, Role.ORG_MANAGER],
        feature: Feature.POLICY,
      },
      {
        path: '/settings/policy/policy-frequencies',
        element: <PolicyFrequencies />,
        role: [Role.ORG_ADMIN, Role.ORG_MANAGER],
        feature: Feature.POLICY,
      },
      {
        path: '/settings/policy/dashboard-benchmarks',
        element: <PolicyDashboardBenchmarks />,
        role: [Role.ORG_ADMIN],
        feature: Feature.POLICY,
      },
      // Task Settings
      {
        path: '/settings/task/task-status',
        element: <TaskStatus />,
        role: [Role.ORG_ADMIN, Role.ORG_MANAGER],
        feature: Feature.PROJECT,
      },

      {
        path: '/settings/task/task-severity',
        element: <TaskSeverity />,
        role: [Role.ORG_ADMIN, Role.ORG_MANAGER],
        feature: Feature.PROJECT,
      },
      // Supply Chain Management Settings
      {
        path: '/settings/service-provider/service-provider-contract-status',
        element: <ServiceProviderContractStatus />,
        role: [Role.ORG_ADMIN, Role.ORG_MANAGER],
        feature: Feature.SERVICE_PROVIDER,
      },
      {
        path: '/settings/service-provider/service-type',
        element: <ServiceType />,
        role: [Role.ORG_ADMIN, Role.ORG_MANAGER],
        feature: Feature.SERVICE_PROVIDER,
      },
      {
        path: '/settings/service-provider/assessment-tier',
        element: <AssessmentTier />,
        role: [Role.ORG_ADMIN, Role.ORG_MANAGER],
        feature: Feature.SERVICE_PROVIDER,
      },
      {
        path: '/settings/service-provider/assessment-tier-criticality',
        element: <AssessmentTierCriticality />,
        role: [Role.ORG_ADMIN, Role.ORG_MANAGER],
        feature: Feature.SERVICE_PROVIDER,
      },
      {
        path: '/settings/service-provider/assessment-tier-sensitivity',
        element: <AssessmentTierSensitivity />,
        role: [Role.ORG_ADMIN, Role.ORG_MANAGER],
        feature: Feature.SERVICE_PROVIDER,
      },
      {
        path: '/settings/service-provider/risk-tier-matrix',
        element: <RiskTierMatrix />,
        role: [Role.ORG_ADMIN, Role.ORG_MANAGER],
        feature: Feature.SERVICE_PROVIDER,
      },
      {
        path: '/settings/service-provider/risk-review-intervals',
        element: <RiskReviewIntervals />,
        role: [Role.ORG_ADMIN, Role.ORG_MANAGER],
        feature: Feature.SERVICE_PROVIDER,
      },
      {
        path: '/settings/service-provider/control-mapping',
        element: <ControlMappingSettings />,
        role: [Role.ORG_ADMIN, Role.ORG_MANAGER],
        feature: Feature.SERVICE_PROVIDER,
      },
      {
        path: '/settings/service-provider/scoring-rubric',
        element: <ScoringRubricSettings />,
        role: [Role.ORG_ADMIN, Role.ORG_MANAGER],
        feature: Feature.SERVICE_PROVIDER,
      },
      // Vendor evaluation detail page
      {
        path: '/service-provider-assessment/:assessmentId/evaluation',
        element: <VendorEvaluation />,
        feature: Feature.SERVICE_PROVIDER,
      },
      // Risk Settings
      {
        path: '/settings/risk/risk-likelihood',
        element: <RiskLikelihood />,
        role: [Role.ORG_ADMIN, Role.ORG_MANAGER],
        feature: Feature.RISK,
      },
      {
        path: '/settings/risk/risk-impact',
        element: <RiskImpact />,
        role: [Role.ORG_ADMIN, Role.ORG_MANAGER],
        feature: Feature.RISK,
      },
      {
        path: '/settings/risk/risk-rating',
        element: <RiskRating />,
        role: [Role.ORG_ADMIN, Role.ORG_MANAGER],
        feature: Feature.RISK,
      },
      {
        path: '/settings/risk/risk-matrix',
        element: <RiskMatrix />,
        role: [Role.ORG_ADMIN, Role.ORG_MANAGER],
        feature: Feature.RISK,
      },
      {
        path: '/settings/risk/risk-categories',
        element: <RiskCategories />,
        role: [Role.ORG_ADMIN, Role.ORG_MANAGER],
        feature: Feature.RISK,
      },
      {
        path: '/settings/risk/treatment',
        element: <RiskTreatmentDecision />,
        role: [Role.ORG_ADMIN, Role.ORG_MANAGER],
        feature: Feature.RISK,
      },
      {
        path: '/settings/risk/incident-types',
        element: <IncidentType />,
        role: [Role.ORG_ADMIN, Role.ORG_MANAGER],
        feature: Feature.RISK,
      },
      {
        path: '/settings/risk/vulnerability',
        element: <Vulnerability />,
        role: [Role.ORG_ADMIN, Role.ORG_MANAGER],
        feature: Feature.RISK,
      },

      {
        path: '/settings/risk/threat-category',
        element: <ThreatCategory />,
        role: [Role.ORG_ADMIN, Role.ORG_MANAGER],
        feature: Feature.RISK,
      },
      {
        path: '/settings/risk/threat-source',
        element: <ThreatSource />,
        role: [Role.ORG_ADMIN, Role.ORG_MANAGER],
        feature: Feature.RISK,
      },
      {
        path: '/settings/risk/threat-actor',
        element: <ThreatActor />,
        role: [Role.ORG_ADMIN, Role.ORG_MANAGER],
        feature: Feature.RISK,
      },
      {
        path: '/settings/risk/threat',
        element: <Threat />,
        role: [Role.ORG_ADMIN, Role.ORG_MANAGER],
        feature: Feature.RISK,
      },
      {
        path: '/settings/risk/risk-identification-source',
        element: <RiskIdentificationSource />,
        role: [Role.ORG_ADMIN, Role.ORG_MANAGER],
        feature: Feature.RISK,
      },
      // Non-Conformity Settings
      {
        path: '/settings/non-conformity/root-identification',
        element: <RootIdentification />,
        role: [Role.ORG_ADMIN, Role.ORG_MANAGER],
        feature: Feature.ASSESSMENTS,
      },
      {
        path: '/settings/non-conformity/affected-area',
        element: <AffectedArea />,
        role: [Role.ORG_ADMIN, Role.ORG_MANAGER],
        feature: Feature.ASSESSMENTS,
      },
      {
        path: '/settings/non-conformity/importance',
        element: <Importance />,
        role: [Role.ORG_ADMIN, Role.ORG_MANAGER],
        feature: Feature.ASSESSMENTS,
      },
      {
        path: '/settings/non-conformity/action-status',
        element: <ActionStatus />,
        role: [Role.ORG_ADMIN, Role.ORG_MANAGER],
        feature: Feature.ASSESSMENTS,
      },
      {
        path: '/settings/information-asset/asset-type',
        element: <AssetType />,
        role: [Role.ORG_ADMIN, Role.ORG_MANAGER],
        feature: Feature.INFORMATION_ASSET,
      },
      {
        path: '/settings/roles-and-permissions',
        element: <RoleAndPermissionSetting />,
        role: [Role.ORG_ADMIN, Role.ORG_MANAGER],
        feature: Feature.USERS,
      },
      {
        path: '/settings/utility',
        element: <UtilitySettings />,
      },
      {
        path: '/settings/field-requirements',
        element: <FieldRequirements />,
        role: [Role.ORG_ADMIN, Role.ORG_MANAGER],
      },
      {
        path: '/settings/utility/toaster',
        element: <ToasterSettings />,
        role: [Role.ORG_ADMIN],
      },
      {
        path: '/settings/external-integration',
        role: [Role.ORG_ADMIN, Role.ORG_MANAGER],
        element: <ExternalIntegrationSettings />,
      },
      {
        path: '/settings/api-tokens',
        element: <ApiTokens />,
      },
      {
        path: '/settings/jira-integration',
        role: [Role.ORG_ADMIN, Role.ORG_MANAGER],
        element: <JiraIntegrationSettings />,
      },
      {
        path: '/settings/azure-integration',
        role: [Role.ORG_ADMIN, Role.ORG_MANAGER],
        feature: Feature.AZURE,
        element: <AzureIntegrationSettings />,
      },
      {
        path: '/settings/confluence-integration',
        role: [Role.ORG_ADMIN, Role.ORG_MANAGER],
        feature: Feature.CONFLUENCE,
        element: <ConfluenceIntegrationSettings />,
      },
      {
        path: '/settings/google-drive-integration',
        role: [Role.ORG_ADMIN, Role.ORG_MANAGER],
        feature: Feature.GOOGLE_DRIVE,
        element: <GoogleDriveIntegrationSettings />,
      },
      {
        path: '/settings/email-configuration',
        role: [Role.ORG_ADMIN],
        element: <EmailConfigurationSettings />,
      },
      {
        path: '/settings/email-configuration/callback',
        role: [Role.ORG_ADMIN],
        element: <EmailConfigurationCallback />,
      },
      {
        path: '/settings/google-drive-integration/callback',
        role: [Role.ORG_ADMIN, Role.ORG_MANAGER],
        feature: Feature.GOOGLE_DRIVE,
        element: <GoogleDriveCallback />,
      },
      {
        path: '/settings/palo-alto-networks-integration',
        role: [Role.ORG_ADMIN, Role.ORG_MANAGER],
        feature: Feature.PALO_ALTO,
        element: <PaloAltoNetworksIntegrationSettings />,
      },
      {
        path: '/settings/ms-defender-integration',
        role: [Role.ORG_ADMIN, Role.ORG_MANAGER],
        feature: Feature.MS_DEFENDER,
        element: <MSDefenderIntegrationSettings />,
      },
      {
        path: '/settings/vanta-integration',
        role: [Role.ORG_ADMIN, Role.ORG_MANAGER],
        feature: Feature.VANTA,
        element: <VantaIntegrationSettings />,
      },
      {
        path: '/settings/drata-integration',
        role: [Role.ORG_ADMIN, Role.ORG_MANAGER],
        feature: Feature.DRATA,
        element: <DrataIntegrationSettings />,
      },
      {
        path: '/settings/mimecast-integration',
        role: [Role.ORG_ADMIN, Role.ORG_MANAGER],
        feature: Feature.MIMECAST,
        element: <MimecastIntegrationSettings />,
      },
      {
        path: '/settings/credential-registry',
        role: [Role.ORG_ADMIN, Role.ORG_MANAGER],
        feature: Feature.ASSESSMENTS,
        element: <CredentialRegistry />,
      },
      {
        path: '/settings/security/password-policy',
        role: [Role.ORG_ADMIN, Role.ORG_MANAGER],
        element: <PasswordPolicyPage />,
      },
      {
        path: '/settings/security/signature-settings',
        role: [Role.ORG_ADMIN, Role.ORG_MANAGER],
        element: <SignatureSettingsPage />,
      },
      {
        path: '/settings/security/sso',
        role: [Role.ORG_ADMIN],
        element: <SsoSettingsPage />,
      },
      {
        path: '/settings/organisation-preference',
        role: [Role.ORG_ADMIN, Role.ORG_MANAGER],
        element: <OrganisationPreference />,
      },
      {
        path: '/settings/pci-scope/people/access-type',
        role: [Role.ORG_ADMIN, Role.ORG_MANAGER],
        feature: Feature.ASSESSMENTS,
        element: <PciScopingConfigurationAccessType />,
      },
      {
        path: '/settings/pci-scope/people/training-type',
        role: [Role.ORG_ADMIN, Role.ORG_MANAGER],
        feature: Feature.ASSESSMENTS,
        element: <PciScopingConfigurationTrainingType />,
      },
      {
        path: '/settings/pci-scope/process/data-handling-type',
        role: [Role.ORG_ADMIN, Role.ORG_MANAGER],
        feature: Feature.ASSESSMENTS,
        element: <PciScopingConfigurationDataHandlingType />,
      },
      {
        path: '/settings/pci-scope/process/frequency-type',
        role: [Role.ORG_ADMIN, Role.ORG_MANAGER],
        feature: Feature.ASSESSMENTS,
        element: <PciScopingConfigurationFrequencyType />,
      },
      {
        path: '/settings/ai-register/type',
        feature: Feature.AI_REGISTER,
        element: <AiRegisterType />,
      },
      {
        path: '/settings/ai-register/permitted-data',
        feature: Feature.AI_REGISTER,
        element: <AiRegisterPermittedData />,
      },
      {
        path: '/settings/ai-register/screening-questionnaire',
        feature: Feature.AI_REGISTER,
        element: <AIScreeningQuestionnaire />,
      },
      {
        path: '/settings/information-security-objectives/status',
        feature: Feature.INFORMATION_SECURITY_OBJECTIVE,
        element: <InformationSecurityObjectiveStatus />,
      },
      {
        path: '/settings/information-security-objectives/priority',
        feature: Feature.INFORMATION_SECURITY_OBJECTIVE,
        element: <InformationSecurityObjectivePriority />,
      },
      {
        path: '/settings/information-security-objectives/reporting-format',
        feature: Feature.INFORMATION_SECURITY_OBJECTIVE,
        element: <InformationSecurityObjectiveReportingFormat />,
      },
      {
        path: '/settings/information-security-objectives/analysis-evaluation-frequency',
        feature: Feature.INFORMATION_SECURITY_OBJECTIVE,
        element: <InformationSecurityObjectiveAnalysisEvaluationFrequency />,
      },
      {
        path: '/settings/information-security-objectives/monitoring-frequency',
        feature: Feature.INFORMATION_SECURITY_OBJECTIVE,
        element: <InformationSecurityObjectiveMonitoringFrequency />,
      },
      {
        path: '/settings/information-security-objectives/measure-type',
        feature: Feature.INFORMATION_SECURITY_OBJECTIVE,
        element: <InformationSecurityObjectiveMeasureType />,
      },
      {
        path: '/settings/information-security-objectives/area',
        feature: Feature.INFORMATION_SECURITY_OBJECTIVE,
        element: <InformationSecurityObjectiveArea />,
      },
      {
        path: '/ai-register/dashboard',
        feature: Feature.AI_REGISTER,
        element: <AIRegisterDashboard />,
      },
      {
        path: '/settings/classification-label',
        element: <ClassificationLabel />,
      },
      {
        path: '/settings/pdf-template',
        element: <PdfTemplateSettingsPage />,
      },
      // ========================================
      // END SETTINGS ROUTES (ORGANISATION)
      // ========================================

      // ========================================
      // START CTRA PROGRAMS ROUTES
      // ========================================
      {
        path: '/ctra/programs',
        element: <CTRAPrograms />,
        feature: Feature.CTRA,
      },
      {
        path: '/ctra/zones',
        element: <CTRAZones />,
        feature: Feature.CTRA,
      },
      {
        path: '/ctra/threat-scenario',
        element: <CTRAThreatScenarioCatalog />,
        feature: Feature.CTRA,
      },
      {
        path: '/ctra/engagements',
        element: <CTRAEngagementList />,
        feature: Feature.CTRA,
      },
      {
        path: '/ctra/engagements/add',
        element: <AddCTRAEngagement />,
        feature: Feature.CTRA,
      },
      {
        path: '/ctra/engagements/add/:id',
        element: <AddCTRAEngagement />,
        feature: Feature.CTRA,
      },
      {
        path: '/ctra/engagements/:id',
        element: <CTRAEngagementOverview />,
        feature: Feature.CTRA,
      },
      {
        path: '/ctra/engagements/:id/promote-risks',
        element: <PromoteCTRARisks />,
        feature: Feature.CTRA,
      },
      {
        path: '/ctra/engagements/:id/add-risk',
        element: <AddCTRARisk />,
        feature: Feature.CTRA,
      },
      {
        path: '/ctra/engagements/:id/add-risk/:riskId',
        element: <AddCTRARisk />,
        feature: Feature.CTRA,
      },
      {
        path: '/ctra/engagements/:id/questionnaire-batch',
        element: <CTRAQuestionnaireBatchWizard />,
        feature: Feature.CTRA,
      },
      {
        path: '/ctra/engagements/:id/questionnaire-batch/:batchId',
        element: <CTRAQuestionnaireBatchWizard />,
        feature: Feature.CTRA,
      },
      {
        path: '/ctra/manage-soa',
        element: <ManageCTRASoA />,
        feature: Feature.CTRA,
      },

      // ========================================
      // END CTRA PROGRAMS ROUTES
      // ========================================

      // START - AI SERVICES ROUTES
      {
        // ONLY FOR AI ENGINEER USE - This will remove after testing is done by AI Engineer
        path: '/organisation/ai/standard-qa',
        element: <StandardQA />,
        role: [Role.ORG_ADMIN],
      },

      // END - AI SERVICES ROUTES

      // 404
      {
        path: '*',
        element: <PageNotFound />,
      },
      {
        path: '/user-manual',
        element: <UserManual />,
      },
    ],
  },
]

export const SuperAdminRoute: IRoute[] = [
  {
    path: '/',
    element: <Outlet />,
    children: [
      // ========================================
      // COMMON ROUTES
      // ========================================
      {
        path: '/',
        element: <DashboardPage />,
      },
      {
        path: '/login-logs',
        element: <LoginLogs />,
      },
      {
        path: '/activity-logs',
        element: <ActivityLogs />,
      },
      {
        path: '/activity-logs/all-users',
        role: [Role.SUPER_ADMIN, Role.ADMIN],
        feature: Feature.USERS,
        element: <ShowAllUserActivityLogs />,
      },
      {
        path: '/activity-analytics',
        role: [Role.SUPER_ADMIN, Role.ADMIN],
        feature: Feature.USERS,
        element: <ActivityAnalytics />,
      },
      {
        path: '/notifications',
        element: <Notifications />,
      },
      {
        path: '/profile',
        element: <Profile />,
      },
      {
        path: '/settings/profile',
        element: <Profile />,
      },
      {
        path: '/settings/mfa',
        element: <MfaSetupPage />,
      },
      {
        path: '/update-password',
        element: <UpdatePassword />,
      },

      // ========================================
      // SUPER ADMIN ROUTES
      // ========================================
      {
        path: '/admin/signup-request',
        element: <SignupRequest />,
        role: [Role.SUPER_ADMIN, Role.ADMIN],
        feature: Feature.ADMIN_ORGANISATION,
      },
      {
        path: '/admin/signup-request/report',
        element: <SignupRequestReport />,
        role: [Role.SUPER_ADMIN, Role.ADMIN],
        feature: Feature.ADMIN_ORGANISATION,
      },
      {
        path: '/admin/organisations',
        element: <AdminManageOrganisation />,
        role: [Role.SUPER_ADMIN, Role.ADMIN],
        feature: Feature.ADMIN_ORGANISATION,
      },
      {
        path: '/admin/dashboard-defaults',
        element: <DashboardDefaults />,
        role: [Role.SUPER_ADMIN, Role.ADMIN],
        feature: Feature.ADMIN_ORGANISATION,
      },
      {
        path: '/admin/organisations/child-organisations/:id',
        element: <AdminManageChildOrganisation />,
        role: [Role.SUPER_ADMIN, Role.ADMIN],
        feature: Feature.ADMIN_ORGANISATION,
      },
      {
        path: '/admin/organisation-users',
        element: <AdminManageOrganisationUsers />,
        role: [Role.SUPER_ADMIN, Role.ADMIN],
        feature: Feature.ADMIN_ORGANISATION,
      },
      {
        path: '/admin/clerk-users',
        element: <ClerkUsers />,
        role: [Role.SUPER_ADMIN, Role.ADMIN],
        feature: Feature.ADMIN_ORGANISATION,
      },
      {
        path: '/admin/assessment',
        element: <Assessment />,
        role: [Role.SUPER_ADMIN, Role.ADMIN],
        feature: Feature.ADMIN_ORGANISATION,
      },
      {
        path: '/admin/admins',
        element: <AdminManageAdmin />,
        role: [Role.SUPER_ADMIN, Role.ADMIN],
        feature: Feature.ADMIN_ADMIN_MANAGE,
      },
      {
        path: '/admin/resources',
        element: <AdminManageResource />,
        role: [Role.SUPER_ADMIN, Role.ADMIN],
        feature: Feature.ADMIN_RESOURCE,
      },
      {
        path: '/admin/user-manual',
        element: <AdminUserManual />,
        role: [Role.SUPER_ADMIN, Role.ADMIN],
        feature: Feature.ADMIN_RESOURCE,
      },
      {
        path: '/admin/mcp-server-extension',
        element: <AdminMCPServerExtension />,
        role: [Role.SUPER_ADMIN, Role.ADMIN],
        feature: Feature.ADMIN_RESOURCE,
      },
      {
        path: '/admin/helpful-tips',
        element: <HelpfulTips />,
        role: [Role.SUPER_ADMIN, Role.ADMIN],
        feature: Feature.ADMIN_RESOURCE,
      },
      {
        path: '/admin/user-guides',
        element: <UserGuides />,
        role: [Role.SUPER_ADMIN, Role.ADMIN],
        feature: Feature.ADMIN_RESOURCE,
      },
      {
        path: '/admin/user-guides/:id',
        element: <UserGuideEditor />,
        role: [Role.SUPER_ADMIN, Role.ADMIN],
        feature: Feature.ADMIN_RESOURCE,
      },
      {
        path: '/admin/user-guide-api-keys',
        element: <UserGuideApiKeys />,
        role: [Role.SUPER_ADMIN, Role.ADMIN],
        feature: Feature.ADMIN_RESOURCE,
      },
      {
        path: '/admin/it-privileged-operations',
        element: <ITOperations />,
        role: [Role.SUPER_ADMIN, Role.ADMIN],
        feature: Feature.ADMIN_IT_PRIVILEGED_OPERATIONS,
      },
      {
        path: '/admin/server-logs',
        element: <ServerLogs />,
        role: [Role.SUPER_ADMIN, Role.ADMIN],
        feature: Feature.ADMIN_IT_PRIVILEGED_OPERATIONS,
      },
      {
        path: '/admin/release-notes',
        element: <ReleaseNote />,
        role: [Role.SUPER_ADMIN, Role.ADMIN],
        feature: Feature.ADMIN_RESOURCE,
      },
      {
        path: '/admin/api-docs-tokens',
        element: <ApiDocsTokens />,
        role: [Role.SUPER_ADMIN, Role.ADMIN],
        feature: Feature.ADMIN_RESOURCE,
      },
      {
        path: '/admin/bug-report',
        element: <AdminManageBugReport />,
        role: [Role.SUPER_ADMIN, Role.ADMIN],
        feature: Feature.ADMIN_RESOURCE,
      },
      {
        path: '/admin/standards',
        element: <AdminManageStandards />,
        role: [Role.SUPER_ADMIN, Role.ADMIN],
        feature: Feature.ADMIN_STANDARDS,
      },
      {
        path: '/admin/saq-types',
        element: <AdminManageSAQTypes />,
        role: [Role.SUPER_ADMIN, Role.ADMIN],
        feature: Feature.ADMIN_STANDARDS,
      },
      {
        path: '/admin/fiscal-years',
        element: <AdminManageFiscalYears />,
        role: [Role.SUPER_ADMIN, Role.ADMIN],
        feature: Feature.ADMIN_STANDARDS,
      },
      {
        path: '/admin/standard-levels',
        element: <AdminManageStandardLevel />,
        role: [Role.SUPER_ADMIN, Role.ADMIN],
        feature: Feature.ADMIN_STANDARDS,
      },
      {
        path: '/admin/control-groups',
        element: <AdminManageControlGroup />,
        role: [Role.SUPER_ADMIN, Role.ADMIN],
        feature: Feature.ADMIN_CONTROLS,
      },
      {
        path: '/admin/controls',
        element: <AdminManageControls />,
        role: [Role.SUPER_ADMIN, Role.ADMIN],
        feature: Feature.ADMIN_CONTROLS,
      },
      {
        path: '/admin/control-mapping',
        element: <AdminManageControlMapping />,
        role: [Role.SUPER_ADMIN, Role.ADMIN],
        feature: Feature.ADMIN_CONTROLS,
      },
      {
        path: '/admin/vendor-baseline',
        element: <AdminVendorBaseline />,
        role: [Role.SUPER_ADMIN, Role.ADMIN],
        feature: Feature.ADMIN_CONTROLS,
      },
      {
        path: '/admin/control-questionnaires',
        element: <AdminManageControlQuestionnaires />,
        role: [Role.SUPER_ADMIN, Role.ADMIN],
        feature: Feature.ADMIN_CONTROLS,
      },
      {
        path: '/admin/maturity-models',
        element: <AdminManageMaturityModels />,
        role: [Role.SUPER_ADMIN, Role.ADMIN],
        feature: Feature.ADMIN_MATURITY_MODELS,
      },
      {
        path: '/admin/maturity-levels',
        element: <AdminManageMaturityLevels />,
        role: [Role.SUPER_ADMIN, Role.ADMIN],
        feature: Feature.ADMIN_MATURITY_MODELS,
      },
      {
        path: '/admin/module',
        element: <Module />,
        role: [Role.SUPER_ADMIN, Role.ADMIN],
        feature: Feature.ADMIN_STANDARDS,
      },
      {
        path: '/admin/pci-dss-task',
        element: <PciDssTask />,
        role: [Role.SUPER_ADMIN, Role.ADMIN],
        feature: Feature.ADMIN_STANDARDS,
      },
      {
        path: '/admin/pci-dss-evidence-pack-task',
        element: <PciDssEvidencePackTask />,
        role: [Role.SUPER_ADMIN, Role.ADMIN],
        feature: Feature.ADMIN_STANDARDS,
      },
      {
        path: '/admin/subscriptions',
        element: <Subscription />,
        role: [Role.SUPER_ADMIN, Role.ADMIN],
        feature: Feature.ADMIN_ORGANISATION,
      },
      {
        path: '/admin/subscription-type',
        element: <SubscriptionType />,
        role: [Role.SUPER_ADMIN, Role.ADMIN],
        feature: Feature.ADMIN_ORGANISATION,
      },
      {
        path: '/admin/fcm-tokens',
        element: <FCMTokens />,
        role: [Role.SUPER_ADMIN, Role.ADMIN],
        feature: Feature.ADMIN_RESOURCE,
      },
      {
        path: '/admin/activity-logs',
        element: <AllOrganisationActivityLogs />,
        role: [Role.SUPER_ADMIN, Role.ADMIN],
        feature: Feature.ADMIN_RESOURCE,
      },
      {
        path: '/admin/email-logs',
        element: <SuperAdminEmailLogs />,
        role: [Role.SUPER_ADMIN, Role.ADMIN],
        feature: Feature.ADMIN_RESOURCE,
      },
      {
        path: '/admin/activity-analytics',
        element: <SuperAdminActivityAnalytics />,
        role: [Role.SUPER_ADMIN, Role.ADMIN],
        feature: Feature.ADMIN_RESOURCE,
      },
      {
        path: '/admin/risk-assessment/system-exposure',
        element: <RiskAssessmentSystemExposure />,
        role: [Role.SUPER_ADMIN, Role.ADMIN],
        feature: Feature.ADMIN_RISK_ASSESSMENT,
      },
      {
        path: '/admin/risk-assessment/hosting-model',
        element: <RiskAssessmentHostingModel />,
        role: [Role.SUPER_ADMIN, Role.ADMIN],
        feature: Feature.ADMIN_RISK_ASSESSMENT,
      },
      {
        path: '/admin/risk-assessment/data-classification',
        element: <RiskAssessmentDataClassification />,
        role: [Role.SUPER_ADMIN, Role.ADMIN],
        feature: Feature.ADMIN_RISK_ASSESSMENT,
      },
      {
        path: '/admin/risk-assessment/user-base',
        element: <RiskAssessmentUserBase />,
        role: [Role.SUPER_ADMIN, Role.ADMIN],
        feature: Feature.ADMIN_RISK_ASSESSMENT,
      },
      {
        path: '/admin/risk-assessment/assessment-type',
        element: <RiskAssessmentType />,
        role: [Role.SUPER_ADMIN, Role.ADMIN],
        feature: Feature.ADMIN_RISK_ASSESSMENT,
      },
      {
        path: '/admin/risk-assessment/questionnaire-library',
        element: <RiskAssessmentQuestionnaire />,
        role: [Role.SUPER_ADMIN, Role.ADMIN],
        feature: Feature.ADMIN_RISK_ASSESSMENT,
      },
      {
        path: '/admin/risk-assessment/questionnaire-library/:questionnaireId',
        element: <RiskAssessmentQuestionnaireBuilder />,
        role: [Role.SUPER_ADMIN, Role.ADMIN],
        feature: Feature.ADMIN_RISK_ASSESSMENT,
      },
      {
        path: '/admin/people-risk-assessment/assessment-type',
        element: <PeopleRiskAssessmentType />,
        role: [Role.SUPER_ADMIN, Role.ADMIN],
        feature: Feature.ADMIN_PEOPLE_RISK_ASSESSMENT,
      },
      {
        path: '/admin/people-risk-assessment/questionnaire-library',
        element: <PeopleRiskAssessmentQuestionnaire />,
        role: [Role.SUPER_ADMIN, Role.ADMIN],
        feature: Feature.ADMIN_PEOPLE_RISK_ASSESSMENT,
      },
      {
        path: '/admin/people-risk-assessment/questionnaire-library/:questionnaireId',
        element: <PeopleRiskAssessmentQuestionnaireBuilder />,
        role: [Role.SUPER_ADMIN, Role.ADMIN],
        feature: Feature.ADMIN_PEOPLE_RISK_ASSESSMENT,
      },
      {
        path: '/admin/ai-models',
        element: <AIModels />,
        role: [Role.SUPER_ADMIN, Role.ADMIN],
        feature: Feature.ADMIN_AI_MODEL,
      },
      {
        path: '/admin/ai-token-usage',
        element: <AITokenUsage />,
        role: [Role.SUPER_ADMIN, Role.ADMIN],
        feature: Feature.ADMIN_AI_MODEL,
      },
      {
        path: '/admin/ai-prompt-catalog',
        element: <AIPromptCatalog />,
        role: [Role.SUPER_ADMIN],
      },
      {
        path: '/admin/assistant-chat-history',
        element: <AssistantChatHistory />,
        role: [Role.SUPER_ADMIN, Role.ADMIN],
        feature: Feature.ADMIN_ORGANISATION,
      },
      {
        path: '/admin/scheduler-config',
        element: <SchedulerConfig />,
        role: [Role.SUPER_ADMIN, Role.ADMIN],
        feature: Feature.ADMIN_RESOURCE,
      },
      {
        path: '/admin/ai-database',
        element: <AiDatabase />,
        role: [Role.SUPER_ADMIN],
        feature: Feature.ADMIN_IT_PRIVILEGED_OPERATIONS,
      },
      {
        path: '/admin/database',
        element: <DatabasePage />,
        role: [Role.SUPER_ADMIN],
        feature: Feature.ADMIN_IT_PRIVILEGED_OPERATIONS,
      },
      {
        path: '/admin/phished-recipient-tag-color',
        element: <PhishedRecipientTagColor />,
        role: [Role.SUPER_ADMIN, Role.ADMIN],
        feature: Feature.ADMIN_RESOURCE,
      },
      {
        path: '/admin/email-template',
        element: <EmailTemplate />,
        role: [Role.SUPER_ADMIN, Role.ADMIN],
        feature: Feature.ADMIN_EMAIL_CUSTOMIZATION,
      },
      {
        path: '/admin/common-pdf-template',
        element: <CommonPdfTemplatePage />,
        role: [Role.SUPER_ADMIN, Role.ADMIN],
        feature: Feature.ADMIN_RESOURCE,
      },

      // START - AI RAG ROUTES
      {
        path: '/admin/ai-rag/agents',
        element: <AiAgents />,
        role: [Role.SUPER_ADMIN],
      },
      {
        path: '/admin/ai-rag/policy-templates',
        element: <AiPolicyTemplates />,
        role: [Role.SUPER_ADMIN],
      },
      {
        path: '/admin/ai-rag/policy-packs',
        element: <AiPolicyPacks />,
        role: [Role.SUPER_ADMIN],
      },
      {
        path: '/admin/ai-rag/control-embeddings',
        element: <ControlEmbeddings />,
        role: [Role.SUPER_ADMIN],
      },
      {
        path: '/admin/ai-rag/evidence-index',
        element: <EvidenceIndex />,
        role: [Role.SUPER_ADMIN],
      },
      {
        path: '/admin/ai-rag/control-mapping',
        element: <ControlMapping />,
        role: [Role.SUPER_ADMIN],
      },
      {
        path: '/admin/ai-rag/activity-log',
        element: <AiActivityLog />,
        role: [Role.SUPER_ADMIN],
      },
      {
        path: '/admin/ai-rag/token-usage',
        element: <AiServiceTokenUsage />,
        role: [Role.SUPER_ADMIN],
      },
      {
        path: '/admin/ai-rag/service-status',
        element: <AiServiceStatus />,
        role: [Role.SUPER_ADMIN],
      },
      // END - AI RAG ROUTES

      // 404
      {
        path: '*',
        element: <PageNotFound />,
      },
      {
        path: '/user-manual',
        element: <UserManual />,
      },
    ],
  },
]
